import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  productService,
  imageUploadService,
  MultilingualString,
} from "../../services/firebaseService";

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: { en: '', my: '' } as MultilingualString,
    description: { en: '', my: '' } as MultilingualString,
    shortDescription: { en: '', my: '' } as MultilingualString,
    image: "",
    price: "",
    category: "",
    features: [] as string[],
    specifications: {} as Record<string, string>,
    inStock: true,
    stockQuantity: 0,
    published: false,
    featured: false,
    index: 0,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [newSpecKey, setNewSpecKey] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");

  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    if (isEdit && id) {
      loadProduct();
    }
  }, [isEdit, id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const product = await productService.getProduct(id!);
      if (product) {
        // Convert to MultilingualString format if it's a string (backward compatibility)
        const normalizeMultilingual = (value: string | MultilingualString): MultilingualString => {
          if (typeof value === 'string') {
            return { en: value, my: '' };
          }
          return value;
        };

        setFormData({
          title: normalizeMultilingual(product.title),
          description: normalizeMultilingual(product.description),
          shortDescription: normalizeMultilingual(product.shortDescription),
          image: product.image,
          price: product.price,
          category: product.category,
          features: product.features,
          specifications: product.specifications,
          inStock: product.inStock,
          stockQuantity: product.stockQuantity,
          published: product.published,
          featured: product.featured,
          index: product.index ?? 0,
        });
        setImagePreview(product.image);
      }
    } catch (error) {
      console.error("Error loading product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    
    // Handle multilingual fields (title.en, title.my, description.en, description.my, shortDescription.en, shortDescription.my)
    if (name.includes('.')) {
      const [field, lang] = name.split('.');
      if (field === 'title' || field === 'description' || field === 'shortDescription') {
        setFormData(prev => ({
          ...prev,
          [field]: {
            ...(prev[field as keyof typeof prev] as MultilingualString),
            [lang]: value
          }
        }));
        return;
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number" && name !== "price"
          ? Number(value)
          : value,
    }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, newFeature.trim()],
      }));
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleAddSpecification = () => {
    if (newSpecKey.trim() && newSpecValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [newSpecKey.trim()]: newSpecValue.trim(),
        },
      }));
      setNewSpecKey("");
      setNewSpecValue("");
    }
  };

  const handleRemoveSpecification = (key: string) => {
    setFormData((prev) => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return {
        ...prev,
        specifications: newSpecs,
      };
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      let imageUrl = formData.image;

      // Upload image if a new file is selected
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await imageUploadService.uploadImage(
            imageFile,
            "products"
          );
          setFormData((prev) => ({ ...prev, image: imageUrl }));
        } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
          setUploading(false);
          setSaving(false);
          return;
        } finally {
          setUploading(false);
        }
      }

      // Update formData with the final image URL
      const finalFormData = { ...formData, image: imageUrl };

      if (isEdit && id) {
        await productService.updateProduct(id, finalFormData);
      } else {
        await productService.createProduct(finalFormData);
      }

      navigate("/admin");
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--gradient-primary)] p-8 flex justify-center items-start md:p-4">
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="text-center">
            <div className="spinner"></div>
            <p className="mt-4">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--gradient-primary)] p-8 flex justify-center items-start md:p-4">
      <div className="bg-white rounded-xl shadow-[0_8px_25px_rgba(0,0,0,0.1)] p-8 w-full max-w-[900px] md:p-6">
        <div className="flex justify-between items-center mb-8 pb-4 border-b-2 border-gray-200 md:flex-col md:gap-4 md:items-start">
          <h1 className="text-[#1e293b] text-[1.75rem] font-bold m-0 md:text-2xl">{isEdit ? "Edit Product" : "Create New Product"}</h1>
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/admin")}
          >
            ← Back to Admin
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-2 text-sm">Title *</label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-xs text-gray-600">English Title</label>
                <input
                  type="text"
                  name="title.en"
                  value={formData.title.en}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  placeholder="Enter product title in English"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-600">Myanmar Title (မြန်မာ)</label>
                <input
                  type="text"
                  name="title.my"
                  value={formData.title.my}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  placeholder="Enter product title in Myanmar"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">

            <div className="flex flex-col">
              <label htmlFor="category" className="font-medium text-gray-700 mb-2 text-sm">
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                required
              >
                <option value="">Select Category</option>
                <option value="electronics">Electronics</option>
                <option value="software">Software</option>
                <option value="hardware">Hardware</option>
                <option value="accessories">Accessories</option>
                <option value="books">Books</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
            <div className="flex flex-col">
              <label htmlFor="price" className="font-medium text-gray-700 mb-2 text-sm">
                Price *
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                placeholder="e.g., $99.99, Free, Contact for pricing"
                required
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="stockQuantity" className="font-medium text-gray-700 mb-2 text-sm">
                Stock Quantity *
              </label>
              <input
                type="number"
                id="stockQuantity"
                name="stockQuantity"
                value={formData.stockQuantity}
                onChange={handleInputChange}
                className="px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                min="0"
                required
              />
            </div>
          </div>

          <div className="flex flex-col">
            <label htmlFor="index" className="font-medium text-gray-700 mb-2 text-sm">
              Display Priority (Index)
            </label>
            <input
              type="number"
              id="index"
              name="index"
              value={formData.index}
              onChange={handleInputChange}
              className="px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              min="0"
              placeholder="0 = highest priority (appears first)"
            />
            <small className="block mt-2 text-[var(--text-secondary)] text-sm">
              Lower numbers appear first in the products list. Products with the
              same index are sorted by creation date.
            </small>
          </div>

          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-2 text-sm">Short Description *</label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-xs text-gray-600">English Short Description</label>
                <textarea
                  name="shortDescription.en"
                  value={formData.shortDescription.en}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={3}
                  placeholder="Brief description in English for listings and cards"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-600">Myanmar Short Description (မြန်မာ)</label>
                <textarea
                  name="shortDescription.my"
                  value={formData.shortDescription.my}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={3}
                  placeholder="Brief description in Myanmar for listings and cards"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-2 text-sm">Full Description *</label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-xs text-gray-600">English Full Description</label>
                <textarea
                  name="description.en"
                  value={formData.description.en}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={6}
                  placeholder="Detailed description in English"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-600">Myanmar Full Description (မြန်မာ)</label>
                <textarea
                  name="description.my"
                  value={formData.description.my}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={6}
                  placeholder="Detailed description in Myanmar"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <label htmlFor="image" className="font-medium text-gray-700 mb-2 text-sm">
              Image *
            </label>
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm cursor-pointer transition-all duration-200 bg-gray-50 hover:border-primary hover:bg-blue-50/50 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              required={!isEdit || !formData.image}
            />
            {imagePreview && (
              <div className="mt-4 flex flex-col gap-2">
                <div className="relative inline-block max-w-[300px] rounded-lg overflow-hidden border-2 border-gray-200">
                  <img src={imagePreview} alt="Preview" className="w-full h-auto block max-h-[300px] object-contain bg-gray-50" />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-xl leading-none p-0 shadow-lg bg-red-500 text-white hover:scale-110 transition-transform z-10"
                    title="Remove image"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-gray-500 m-0">
                  {imageFile ? `Selected: ${imageFile.name}` : "Current image"}
                </p>
              </div>
            )}
            {!imagePreview && !imageFile && isEdit && formData.image && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
                <p className="m-0">Current image will be kept if no new image is selected.</p>
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-2 text-sm">Features</label>
            <div className="flex flex-col gap-4">
              <div className="flex gap-2 items-center md:flex-col md:items-stretch">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  placeholder="Add a feature"
                  onKeyPress={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), handleAddFeature())
                  }
                />
                <button
                  type="button"
                  onClick={handleAddFeature}
                  className="btn btn-primary"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-md text-sm">
                    <span className="text-gray-700">{feature}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="btn btn-danger btn-sm"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-2 text-sm">Specifications</label>
            <div className="flex flex-col gap-4">
              <div className="flex gap-2 items-center md:flex-col md:items-stretch md:gap-3">
                <input
                  type="text"
                  value={newSpecKey}
                  onChange={(e) => setNewSpecKey(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  placeholder="Specification name"
                />
                <input
                  type="text"
                  value={newSpecValue}
                  onChange={(e) => setNewSpecValue(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  placeholder="Specification value"
                />
                <button
                  type="button"
                  onClick={handleAddSpecification}
                  className="btn btn-primary"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(formData.specifications).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-2 rounded-md text-sm">
                    <span className="font-medium text-gray-700">{key}:</span>
                    <span className="text-gray-500">{value}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSpecification(key)}
                      className="btn btn-danger btn-sm"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-8 my-4 flex-wrap md:flex-col md:gap-4">
            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="inStock"
                checked={formData.inStock}
                onChange={handleInputChange}
                className="w-[18px] h-[18px] accent-primary"
              />
              In Stock
            </label>

            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
                className="w-[18px] h-[18px] accent-primary"
              />
              Published
            </label>

            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleInputChange}
                className="w-[18px] h-[18px] accent-primary"
              />
              Featured
            </label>
          </div>

          <div className="flex gap-4 justify-end mt-8 pt-8 border-t border-gray-200 md:flex-col">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/admin")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || uploading}
            >
              {uploading
                ? "Uploading image..."
                : saving
                ? "Saving..."
                : isEdit
                ? "Update Product"
                : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
